import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import KanbanBoard from './components/KanbanBoard';
import TableView from './components/TableView';
import ApplicationModal from './components/ApplicationModal';
import ApplicationDetail from './components/ApplicationDetail';
import ContactsPage from './components/ContactsPage';
import DocumentsPage from './components/DocumentsPage';
import TemplatesPage from './components/TemplatesPage';
import AIEmailPage from './components/AIEmailPage';
import HomePage from './components/HomePage';
import AuthPage from './components/AuthPage';
import PrivacyPage from './components/PrivacyPage';
import './App.css';

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('applyai_user')); } catch { return null; }
}

function RequireAuth({ children }) {
  const token = localStorage.getItem('applyai_token');
  return token ? children : <Navigate to="/login" replace />;
}

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const NavIcon = {
  tracker: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  contacts: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
    </svg>
  ),
  templates: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  ai: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L13.09 8.26L19 6L15.74 11L22 12L15.74 13L19 18L13.09 15.74L12 22L10.91 15.74L5 18L8.26 13L2 12L8.26 11L5 6L10.91 8.26L12 2Z"/>
    </svg>
  ),
  documents: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
      <line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/>
    </svg>
  ),
};

function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const page = location.pathname.replace(/^\//, '') || 'dashboard';
  const user = getStoredUser();

  const [navTooltip, setNavTooltip] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const doSignOut = () => {
    localStorage.removeItem('applyai_token');
    localStorage.removeItem('applyai_user');
    navigate('/');
  };

  const [view, setView] = useState('table');
  const [applications, setApplications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [detailApp, setDetailApp] = useState(null);
  const [openContactId, setOpenContactId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const COLLAPSED_W = 56;
  const DEFAULT_W   = 220;
  const MIN_W       = 56;
  const MAX_W       = 320;

  const [sidebarWidth,  setSidebarWidth]  = useState(COLLAPSED_W); // collapsed by default
  const [isResizing,    setIsResizing]    = useState(false);
  const isOpen        = sidebarWidth > 72;
  const lastOpenWidth = useRef(DEFAULT_W);
  const resizeRef     = useRef({ active: false, startX: 0, startWidth: 0 });

  const toggleSidebar = () => {
    if (isOpen) {
      lastOpenWidth.current = sidebarWidth;
      setSidebarWidth(COLLAPSED_W);
    } else {
      setSidebarWidth(lastOpenWidth.current);
    }
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!resizeRef.current.active) return;
      const delta    = e.clientX - resizeRef.current.startX;
      const newWidth = Math.min(MAX_W, Math.max(MIN_W, resizeRef.current.startWidth + delta));
      setSidebarWidth(newWidth);
    };
    const onUp = () => {
      if (!resizeRef.current.active) return;
      resizeRef.current.active = false;
      setIsResizing(false);
      setSidebarWidth(prev => {
        if (prev < 80) return COLLAPSED_W;
        lastOpenWidth.current = prev;
        return prev;
      });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
  }, []); // eslint-disable-line
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (detailApp) {
      const fresh = applications.find(a => a.id === detailApp.id);
      if (fresh) setDetailApp(fresh);
    }
  }, [applications]); // eslint-disable-line

  const fetchAll = async () => {
    try {
      const [appsRes, contactsRes, templatesRes] = await Promise.all([
        fetch(`${API_URL}/api/applications`),
        fetch(`${API_URL}/api/contacts`),
        fetch(`${API_URL}/api/templates`),
      ]);
      const [apps, ctcts, tmpls] = await Promise.all([
        appsRes.json(), contactsRes.json(), templatesRes.json(),
      ]);
      setApplications(Array.isArray(apps) ? apps : []);
      setContacts(Array.isArray(ctcts) ? ctcts : []);
      setTemplates(Array.isArray(tmpls) ? tmpls : []);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateApp = useCallback((updated) => {
    setApplications(prev => prev.map(a => a.id === updated.id ? updated : a));
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this application?')) return;
    await fetch(`${API_URL}/api/applications/${id}`, { method: 'DELETE' });
    setApplications(prev => prev.filter(a => a.id !== id));
    if (detailApp?.id === id) setDetailApp(null);
  }, [detailApp]);

  const patchFields = useCallback(async (id, fields) => {
    setApplications(prev => prev.map(a => a.id === id ? { ...a, ...fields } : a));
    try {
      const res = await fetch(`${API_URL}/api/applications/${id}/fields`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      if (res.ok) updateApp(await res.json());
    } catch { /* optimistic state already applied */ }
  }, [updateApp]);

  const handleStatusChange = useCallback((id, newStatus) => patchFields(id, { status: newStatus }), [patchFields]);

  const handleRatingChange = useCallback(async (id, rating) => {
    setApplications(prev => prev.map(a => a.id === id ? { ...a, excitement_rating: rating } : a));
    const res = await fetch(`${API_URL}/api/applications/${id}/rating`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rating }),
    });
    if (res.ok) updateApp(await res.json());
  }, [updateApp]);

  const handleFieldChange = useCallback((id, fields) => {
    const PATCH_SAFE = ['status','remote_type','job_type','location','salary','job_url','applied_date','deadline','company','role'];
    const canPatch = Object.keys(fields).every(k => PATCH_SAFE.includes(k));
    if (canPatch) return patchFields(id, fields);
    setApplications(prev => prev.map(a => a.id === id ? { ...a, ...fields } : a));
    const current = applications.find(a => a.id === id);
    return fetch(`${API_URL}/api/applications/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...current, ...fields }),
    }).then(res => { if (res.ok) res.json().then(updateApp); }).catch(() => {});
  }, [applications, updateApp, patchFields]);

  const handleArchive = useCallback((id) => patchFields(id, { status: 'Archived' }), [patchFields]);

  const handleSave = async (formData) => {
    if (editingApp) {
      const res = await fetch(`${API_URL}/api/applications/${editingApp.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      updateApp(await res.json());
    } else {
      const res = await fetch(`${API_URL}/api/applications`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const created = await res.json();
      setApplications(prev => [created, ...prev]);
    }
    setModalOpen(false);
  };

  const HIDDEN_BY_DEFAULT = ['Archived', 'Trashed'];
  const activeApps = applications.filter(a => !HIDDEN_BY_DEFAULT.includes(a.status));

  const counts = {
    total: activeApps.length,
    saved: applications.filter(a => a.status === 'Saved').length,
    applied: applications.filter(a => a.status === 'Applied').length,
    screen: applications.filter(a => a.status === 'Screen').length,
    interviewing: applications.filter(a => a.status === 'Interviewing').length,
    offer: applications.filter(a => a.status === 'Offer').length,
    accepted: applications.filter(a => a.status === 'Accepted').length,
    rejected: applications.filter(a => a.status === 'Rejected').length,
    withdrawn: applications.filter(a => a.status === 'Withdrawn').length,
    ghosted: applications.filter(a => a.status === 'Ghosted').length,
    archived: applications.filter(a => a.status === 'Archived').length,
    trashed: applications.filter(a => a.status === 'Trashed').length,
  };
  const responseRate = counts.total
    ? Math.round(((counts.interviewing + counts.offer + counts.accepted) / counts.total) * 100)
    : 0;

  const filteredApps = applications
    .filter(a => {
      if (filterStatus === 'All') return showArchived ? a.status === 'Archived' : !HIDDEN_BY_DEFAULT.includes(a.status);
      return a.status === filterStatus;
    })
    .filter(a => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (a.company || '').toLowerCase().includes(q) || (a.role || '').toLowerCase().includes(q);
    });

  const exportCSV = () => {
    const cols = ['Company', 'Role', 'Status', 'Location', 'Salary', 'Work Mode', 'Job Type', 'Applied Date', 'Degree', 'Skills', 'Job URL'];
    const rows = filteredApps.map(a => [
      a.company, a.role, a.status, a.location, a.salary,
      a.remote_type, a.job_type, a.applied_date, a.required_degree,
      Array.isArray(a.keywords) ? a.keywords.join('; ') : '',
      a.job_url,
    ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`));
    const csv = [cols.map(c => `"${c}"`), ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `applyai-jobs-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click(); URL.revokeObjectURL(url);
  };

  const NAV = [
    { path: '/dashboard', icon: NavIcon.tracker,   label: 'Job Tracker' },
    { path: '/contacts',  icon: NavIcon.contacts,  label: 'Contacts' },
    { path: '/documents', icon: NavIcon.documents, label: 'Documents' },
    { path: '/templates', icon: NavIcon.templates, label: 'Templates' },
    { path: '/ai-email',  icon: NavIcon.ai,        label: 'AI Email' },
  ];

  return (
    <div className="app-shell">
      {/* Sidebar */}
      <aside
        className={`sidebar ${isOpen ? '' : 'collapsed'} ${isResizing ? 'resizing' : ''}`}
        style={{ width: sidebarWidth }}
      >
        <div className="sidebar-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} title="Go to home">
          <span className="logo-badge">AI</span>
          {isOpen && <span className="logo-name">ApplyAI</span>}
        </div>

        <nav className="sidebar-nav">
          {NAV.map(item => (
            <button
              key={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
              onMouseEnter={!isOpen ? (e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setNavTooltip({ label: item.label, top: rect.top + rect.height / 2 });
              } : undefined}
              onMouseLeave={!isOpen ? () => setNavTooltip(null) : undefined}
            >
              <span className="nav-icon">{item.icon}</span>
              {isOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {isOpen && (
          <div className="sidebar-stats">
            <div className="mini-stat-row">
              <span className="mini-stat-label">Active</span>
              <span className="mini-stat-val">{counts.total}</span>
            </div>
            <div className="mini-stat-row">
              <span className="mini-stat-label">Interviewing</span>
              <span className="mini-stat-val amber">{counts.interviewing}</span>
            </div>
            <div className="mini-stat-row">
              <span className="mini-stat-label">Offers</span>
              <span className="mini-stat-val green">{counts.offer + counts.accepted}</span>
            </div>
            <div className="mini-stat-row">
              <span className="mini-stat-label">Response</span>
              <span className="mini-stat-val purple">{responseRate}%</span>
            </div>
          </div>
        )}

        {isOpen && user && (
          <div className="sidebar-user">
            {user.avatar_url
              ? <img src={user.avatar_url} alt="" className="sidebar-user-avatar" style={{ objectFit: 'cover' }} referrerPolicy="no-referrer" />
              : <div className="sidebar-user-avatar">{(user.name || user.email || '?')[0].toUpperCase()}</div>
            }
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user.name || 'User'}</div>
              <div className="sidebar-user-email">{user.email}</div>
            </div>
            <button className="sidebar-signout" onClick={() => setShowLogoutConfirm(true)} title="Sign out">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        )}

        <button className="sidebar-toggle" onClick={toggleSidebar} title="Toggle sidebar">
          {isOpen ? '◀' : '▶'}
        </button>

        {/* Drag-to-resize handle */}
        <div
          className="sidebar-resize-handle"
          onMouseDown={e => {
            e.preventDefault();
            resizeRef.current = { active: true, startX: e.clientX, startWidth: sidebarWidth };
            setIsResizing(true);
          }}
          title="Drag to resize"
        />
      </aside>

      {/* Collapsed sidebar nav tooltip */}
      {navTooltip && !isOpen && (
        <div className="nav-tooltip-fixed" style={{ top: navTooltip.top }}>
          {navTooltip.label}
        </div>
      )}

      {/* Logout confirmation */}
      {showLogoutConfirm && (
        <div className="modal-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="logout-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="logout-confirm-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </div>
            <h3 className="logout-confirm-title">Sign out?</h3>
            <p className="logout-confirm-body">You'll need to sign in again to access your tracker.</p>
            <div className="logout-confirm-actions">
              <button className="btn-secondary" onClick={() => setShowLogoutConfirm(false)}>Cancel</button>
              <button className="btn-danger" onClick={doSignOut}>Sign Out</button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="main-content">
        {/* ── Dashboard ── */}
        {page === 'dashboard' && (
          <>
            <div className="page-header">
              <div>
                <h2 className="page-title">Job Tracker</h2>
                <p className="page-sub">{counts.total} active · {responseRate}% response rate</p>
              </div>
              <div className="header-actions">
                <div className="view-toggle">
                  <button className={view === 'table' ? 'active' : ''} onClick={() => setView('table')}>List</button>
                  <button className={view === 'kanban' ? 'active' : ''} onClick={() => setView('kanban')}>Gallery</button>
                </div>
                <button className="btn-secondary" onClick={exportCSV} title="Export visible jobs as CSV" style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.82rem' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Export CSV
                </button>
                <button className="btn-primary" onClick={() => { setEditingApp(null); setModalOpen(true); }}>
                  + Add Job
                </button>
              </div>
            </div>

            <div className="filter-bar">
              <input
                className="search-input"
                placeholder="🔍  Search company or role..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <div className="filter-pills">
                {[
                  { key: 'All', label: 'All' },
                  { key: 'Saved', label: `Saved ${counts.saved || 0}` },
                  { key: 'Applied', label: `Applied ${counts.applied || 0}` },
                  { key: 'Screen', label: `Screen ${counts.screen || 0}` },
                  { key: 'Interviewing', label: `Interviewing ${counts.interviewing || 0}` },
                  { key: 'Offer', label: `Offer ${counts.offer || 0}` },
                  { key: 'Accepted', label: `Accepted ${counts.accepted || 0}` },
                  { key: 'Rejected', label: `Rejected ${counts.rejected || 0}` },
                  { key: 'Withdrawn', label: `Withdrawn ${counts.withdrawn || 0}` },
                  { key: 'Ghosted', label: `Ghosted ${counts.ghosted || 0}` },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    className={`filter-pill ${filterStatus === key ? 'active' : ''} ${key.toLowerCase()}`}
                    onClick={() => { setFilterStatus(key); setShowArchived(false); }}
                  >
                    {label}
                  </button>
                ))}
                {counts.archived > 0 && (
                  <button
                    className={`filter-pill ${showArchived ? 'active archived' : ''}`}
                    onClick={() => { setShowArchived(v => !v); setFilterStatus('All'); }}
                  >
                    📦 Archived {counts.archived}
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading applications...</div>
            ) : view === 'kanban' ? (
              <KanbanBoard
                applications={filteredApps}
                contacts={contacts}
                onEdit={app => { setEditingApp(app); setModalOpen(true); }}
                onDelete={handleDelete}
                onArchive={handleArchive}
                onStatusChange={handleStatusChange}
                onRatingChange={handleRatingChange}
                onFieldChange={handleFieldChange}
                onCardClick={setDetailApp}
                selectedId={detailApp?.id}
              />
            ) : (
              <TableView
                applications={filteredApps}
                contacts={contacts}
                onEdit={app => { setEditingApp(app); setModalOpen(true); }}
                onDelete={handleDelete}
                onArchive={handleArchive}
                onStatusChange={handleStatusChange}
                onRatingChange={handleRatingChange}
                onFieldChange={handleFieldChange}
                onRowClick={setDetailApp}
                onContactClick={c => { setOpenContactId(c.id); navigate('/contacts'); }}
                API_URL={API_URL}
                onUpdate={updateApp}
                selectedId={detailApp?.id}
              />
            )}
          </>
        )}

        {page === 'contacts' && (
          <ContactsPage
            contacts={contacts}
            applications={applications}
            API_URL={API_URL}
            onUpdate={setContacts}
            openContactId={openContactId}
            onContactOpened={() => setOpenContactId(null)}
            onViewJob={(appId) => {
              const app = applications.find(a => a.id === appId);
              if (app) { navigate('/dashboard'); setDetailApp(app); }
            }}
          />
        )}

        {page === 'documents' && (
          <DocumentsPage API_URL={API_URL} />
        )}

        {page === 'templates' && (
          <TemplatesPage templates={templates} API_URL={API_URL} onUpdate={setTemplates} />
        )}

        {page === 'ai-email' && (
          <AIEmailPage applications={applications} contacts={contacts} API_URL={API_URL} />
        )}
      </main>

      {detailApp && (
        <ApplicationDetail
          app={detailApp}
          contacts={contacts.filter(c => String(c.application_id) === String(detailApp.id))}
          allContacts={contacts}
          templates={templates}
          API_URL={API_URL}
          onClose={() => setDetailApp(null)}
          onUpdate={updateApp}
          onEdit={app => { setEditingApp(app); setModalOpen(true); }}
          onDelete={handleDelete}
          onContactsChange={setContacts}
        />
      )}


      {modalOpen && (
        <ApplicationModal
          application={editingApp}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

export default function App() {
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => document.documentElement.classList.toggle('dark', mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/signup" element={<AuthPage />} />
      <Route path="/dashboard" element={<RequireAuth><AppShell /></RequireAuth>} />
      <Route path="/contacts"  element={<RequireAuth><AppShell /></RequireAuth>} />
      <Route path="/documents" element={<RequireAuth><AppShell /></RequireAuth>} />
      <Route path="/templates" element={<RequireAuth><AppShell /></RequireAuth>} />
      <Route path="/ai-email"  element={<RequireAuth><AppShell /></RequireAuth>} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
